// hooks/useCompanyData.js
import { useState, useEffect, useCallback } from "react";
import { 
  getCompany, 
  getCompanyRoles, 
  getCompanyDocs, 
  getCompanyReviews,
  getCompanyAddresses,
  getCompanyBankAccounts,
  getCompanyNotes,
  getCompanyStatusHistory,
  getDeviezenPermits,
  getCompanyTaxYears,
  getScreenings,
  getDocRequirements
} from "../api";
import { parseArray, toYN } from "../utils/helpers";

export const useCompanyData = (companyId) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [company, setCompany] = useState(null);
  const [roles, setRoles] = useState([]);
  const [docs, setDocs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [devPermits, setDevPermits] = useState([]);
  const [taxYears, setTaxYears] = useState([]);
  const [screenings, setScreenings] = useState([]);
  const [requirements, setRequirements] = useState([]);

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    setError("");
    
    try {
      const companyData = await getCompany(companyId);
      companyData.transaction_types = parseArray(companyData.transaction_types);
      setCompany(companyData);

      const [
        rolesData, docsData, reviewsData, addressesData, 
        bankAccountsData, notesData, statusHistoryData,
        devPermitsData, taxYearsData, screeningsData
      ] = await Promise.allSettled([
        getCompanyRoles(companyId),
        getCompanyDocs(companyId),
        getCompanyReviews(companyId),
        getCompanyAddresses(companyId),
        getCompanyBankAccounts(companyId),
        getCompanyNotes(companyId),
        getCompanyStatusHistory(companyId),
        getDeviezenPermits(companyId),
        getCompanyTaxYears(companyId),
        getScreenings(companyId),
      ]);

      // Handle results, using empty arrays as fallbacks for failed requests
      setRoles(rolesData.status === 'fulfilled' ? rolesData.value : []);
      setDocs(docsData.status === 'fulfilled' ? docsData.value : []);
      setReviews(reviewsData.status === 'fulfilled' ? reviewsData.value : []);
      setAddresses(addressesData.status === 'fulfilled' ? addressesData.value : []);
      setBankAccounts(bankAccountsData.status === 'fulfilled' ? bankAccountsData.value : []);
      setNotes(notesData.status === 'fulfilled' ? notesData.value : []);
      setStatusHistory(statusHistoryData.status === 'fulfilled' ? statusHistoryData.value : []);
      setDevPermits(devPermitsData.status === 'fulfilled' ? devPermitsData.value : []);
      setTaxYears(taxYearsData.status === 'fulfilled' ? taxYearsData.value : []);
      setScreenings(screeningsData.status === 'fulfilled' ? screeningsData.value : []);

      // Load requirements based on company data
      try {
        const requirementsData = await getDocRequirements({
          client_type: "company",
          legal_form: companyData.legal_form || "",
          residency_status: companyData.residency_status || "",
          activity: companyData.business_activity || "",
        });
        setRequirements(requirementsData);
      } catch (reqError) {
        console.warn("Failed to load requirements:", reqError);
        setRequirements([]);
      }

    } catch (err) {
      setError(err.message || "Failed to load company data");
      setCompany(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return {
    loading,
    error,
    company,
    roles,
    docs,
    reviews,
    addresses,
    bankAccounts,
    notes,
    statusHistory,
    devPermits,
    taxYears,
    screenings,
    requirements,
    refetch: loadAll
  };
};